import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useWishlistContext } from '@/account/context/wishlist';
import EVENTS from '@/utils/Constants/Events';
import { notifySuccess } from '@/utils/Common/Notify';
import { goToMyAccountWishlist } from '@/utils/Common/Redirects';
import GigyaLoginPopupController from '@/utils/Controllers/Gigya/GigyaLoginPopupController';

// import api
import isAuth from '@/api/get/isAuth';

// import components
import WishlistProduct from '@/components/organisms/WishlistProduct';
import { emitGtmListingImpression } from '@/utils/Controllers/GTM/Operations/ListingImpression';

const WishlistProductWrapper = ({ entries, hasDelete }) => {
  const { t } = useTranslation();
  const [, { deleteWishlistEntry, notifyProduct }] = useWishlistContext();
  useEffect(() => {
    emitGtmListingImpression();
  }, []);

  const remove = wishlistProductCode => {
    const successCB = () => {
      deleteWishlistEntry(wishlistProductCode);
      notifySuccess(t('wishlist:wishlist.page.removed.entry.success'));
    };
    DLG.EVE.emit(EVENTS.WISHLIST.ENTRY.DELETE, {
      wishlistProductCode,
      successCB,
    });
  };

  const notify = async productCodePost => {
    const isLogged = await isAuth();
    const notifyWishlistEntry = ({ wRedirect = false } = {}) => {
      const successCB = () => {
        if (wRedirect) {
          goToMyAccountWishlist();
        } else {
          notifyProduct(productCodePost);
          notifySuccess(t('wishlist:wishlist.page.notify.entry.success'));
        }
      };
      DLG.EVE.emit(EVENTS.NOTIFY.PRODUCT, {
        productCodePost,
        successCB,
      });
    };

    if (isLogged) {
      notifyWishlistEntry();
    } else {
      GigyaLoginPopupController.init({
        successCB: () => notifyWishlistEntry({ wRedirect: true }),
        header: t('notify.product.login.popup.header'),
        subHeader: t('notify.product.login.popup.subheader'),
      });
    }
  };

  const add = skuCode => {
    DLG.EVE.emit(EVENTS.CART.ADD.PRODUCT, { skuCode });
  };

  return entries.map((entry, i) => (
    <WishlistProduct
      key={entry.code}
      product={entry}
      addEntry={add}
      removeEntry={remove}
      notifyEntry={notify}
      hasDelete={hasDelete}
      position={i + 1}
    />
  ));
};

WishlistProductWrapper.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.shape()),
  hasDelete: PropTypes.bool,
};

WishlistProductWrapper.defaultProps = {
  entries: [],
  hasDelete: true,
};

export default WishlistProductWrapper;
